import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Gift, Trophy, TrendingUp, Users, ArrowRight, Settings,
  ChevronDown, ChevronUp, ExternalLink, Phone, Calendar,
} from 'lucide-react';

const ORDER_STATUS_STYLES = {
  draft: { bg: 'rgba(100,116,139,0.10)', text: '#64748B' },
  confirmed: { bg: 'rgba(37,99,235,0.10)', text: '#2563EB' },
  fabric_ordered: { bg: 'rgba(168,85,247,0.10)', text: '#A855F7' },
  fabric_received: { bg: 'rgba(6,182,212,0.10)', text: '#06B6D4' },
  in_production: { bg: 'rgba(245,158,11,0.10)', text: '#F59E0B' },
  ready_for_trial: { bg: 'rgba(236,72,153,0.10)', text: '#EC4899' },
  alterations_in_progress: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  ready_for_delivery: { bg: 'rgba(34,197,94,0.10)', text: '#22C55E' },
  delivered: { bg: 'rgba(27,42,74,0.10)', text: '#1B2A4A' },
  cancelled: { bg: 'rgba(239,68,68,0.10)', text: '#EF4444' },
};

const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

export default function ReferralsPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [referralList, setReferralList] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(0);
  const [monthsFilter, setMonthsFilter] = useState('6');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leaderboardRes, listRes, funnelRes, trendsRes] = await Promise.all([
        api.get('/referrals/leaderboard?limit=10'),
        api.get(`/referrals/list?skip=${listPage * 20}&limit=20`),
        api.get('/referrals/funnel'),
        api.get(`/referrals/trends?months=${monthsFilter}`),
      ]);
      setLeaderboard(leaderboardRes.data || []);
      setReferralList(listRes.data?.referrals || []);
      setListTotal(listRes.data?.total || 0);
      setFunnel(funnelRes.data);
      setTrends(trendsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
    } finally {
      setLoading(false);
    }
  }, [listPage, monthsFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const funnelStages = funnel?.stages || [];
  const maxFunnelCount = Math.max(...funnelStages.map(s => s.count), 1);

  return (
    <div className="space-y-6" data-testid="referrals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold flex items-center gap-3" style={{ color: 'var(--brand-navy)' }}>
            <Gift className="w-8 h-8" style={{ color: 'var(--brand-gold)' }} />
            Referrals
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
            Track referral performance and conversion
          </p>
        </div>
        <Link to="/app/settings" data-testid="referral-settings-link">
          <Button variant="outline" className="gap-2">
            <Settings className="w-4 h-4" />
            Referral Tiers
          </Button>
        </Link>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Users className="w-4 h-4" />
              Total Referred
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: 'var(--brand-navy)' }}>
              {listTotal}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Trophy className="w-4 h-4" />
              Top Referrers
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: 'var(--brand-navy)' }}>
              {leaderboard.length}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <TrendingUp className="w-4 h-4" />
              Conversion Rate
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: 'var(--brand-gold)' }}>
              {funnel?.conversion_rates?.referred_to_consultation || 0}%
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Gift className="w-4 h-4" />
              Total Revenue
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: '#1E7A5F' }}>
              {formatCurrency(leaderboard.reduce((sum, r) => sum + r.revenue_generated, 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="referral-leaderboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Trophy className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>
                No referrals recorded yet
              </p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((referrer, idx) => (
                  <div key={referrer.referrer_id} className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:border-border transition-colors">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm"
                      style={{
                        background: idx === 0 ? 'linear-gradient(135deg, #C5A55A, #E8D69C)' : idx === 1 ? 'linear-gradient(135deg, #94A3B8, #CBD5E1)' : idx === 2 ? 'linear-gradient(135deg, #CD7F32, #DDA15E)' : 'var(--surface-2)',
                        color: idx < 3 ? 'white' : 'var(--brand-navy)',
                      }}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/app/clients/${referrer.referrer_id}`} className="font-medium text-sm hover:underline" style={{ color: 'var(--brand-navy)' }}>
                        {referrer.referrer_name}
                      </Link>
                      <div className="text-xs flex items-center gap-2" style={{ color: 'var(--ink-muted)' }}>
                        <Phone className="w-3 h-3" />
                        {referrer.referrer_phone || '-'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>
                        {referrer.referral_count} referrals
                      </div>
                      <div className="text-xs" style={{ color: '#1E7A5F' }}>
                        {formatCurrency(referrer.revenue_generated)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="referral-funnel">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {funnelStages.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>
                No funnel data available
              </p>
            ) : (
              <div className="space-y-3">
                {funnelStages.map((stage, idx) => (
                  <div key={stage.stage} className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium" style={{ color: 'var(--brand-navy)' }}>
                        {stage.label}
                      </span>
                      <span className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>
                        {stage.count}
                      </span>
                    </div>
                    <div className="h-8 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                      <div className="h-full rounded-lg transition-all duration-500"
                        style={{
                          width: `${(stage.count / maxFunnelCount) * 100}%`,
                          background: `linear-gradient(90deg, var(--brand-gold), rgba(197,165,90,0.6))`,
                          minWidth: stage.count > 0 ? '20px' : '0',
                        }} />
                    </div>
                    {idx < funnelStages.length - 1 && funnel?.conversion_rates && (
                      <div className="flex items-center justify-center my-1">
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                        <span className="text-xs ml-1" style={{ color: 'var(--ink-muted)' }}>
                          {Object.values(funnel.conversion_rates)[idx]}% conversion
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="referral-trends">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Calendar className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            Monthly Trends
          </CardTitle>
          <Select value={monthsFilter} onValueChange={setMonthsFilter}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {trends.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>
              No trend data available
            </p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-5 text-xs font-medium pb-2 border-b" style={{ color: 'var(--ink-muted)' }}>
                <span>Month</span>
                <span className="text-center">Referrals</span>
                <span className="text-center">Conversions</span>
                <span className="text-center">Rate</span>
                <span className="text-right">Revenue</span>
              </div>
              {trends.map(t => (
                <div key={t.month} className="grid grid-cols-5 text-sm py-2 border-b border-border/30">
                  <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>
                    {new Date(t.month + '-01').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                  </span>
                  <span className="text-center" style={{ color: 'var(--ink-muted)' }}>{t.referrals}</span>
                  <span className="text-center" style={{ color: '#1E7A5F' }}>{t.conversions}</span>
                  <span className="text-center">
                    <Badge variant="outline" className="text-xs" style={{
                      background: t.conversion_rate > 50 ? 'rgba(34,197,94,0.10)' : t.conversion_rate > 25 ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.10)',
                      color: t.conversion_rate > 50 ? '#22C55E' : t.conversion_rate > 25 ? '#F59E0B' : '#EF4444',
                      borderColor: 'transparent',
                    }}>
                      {t.conversion_rate}%
                    </Badge>
                  </span>
                  <span className="text-right font-medium" style={{ color: 'var(--brand-navy)' }}>
                    {formatCurrency(t.revenue)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral List */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="referral-list">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Users className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            All Referrals
            <Badge variant="outline" className="ml-2 text-xs">{listTotal}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referralList.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>
              No referrals found
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Referrer</TableHead>
                    <TableHead className="text-xs">→</TableHead>
                    <TableHead className="text-xs">Referred Client</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">First Order</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralList.map(ref => {
                    const orderStatus = ref.first_order?.status;
                    const statusStyle = ORDER_STATUS_STYLES[orderStatus] || ORDER_STATUS_STYLES.draft;
                    return (
                      <TableRow key={ref.referred_client_id} data-testid={`referral-row-${ref.referred_client_id}`}>
                        <TableCell>
                          <Link to={`/app/clients/${ref.referrer_id}`} className="text-sm font-medium hover:underline" style={{ color: 'var(--brand-navy)' }}>
                            {ref.referrer_name}
                          </Link>
                          <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>{ref.referrer_phone}</div>
                        </TableCell>
                        <TableCell>
                          <ArrowRight className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
                        </TableCell>
                        <TableCell>
                          <Link to={`/app/clients/${ref.referred_client_id}`} className="text-sm font-medium hover:underline" style={{ color: 'var(--brand-navy)' }}>
                            {ref.referred_client_name}
                          </Link>
                          <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>{ref.referred_client_phone}</div>
                        </TableCell>
                        <TableCell className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                          {formatDate(ref.referred_at)}
                        </TableCell>
                        <TableCell>
                          {ref.first_order ? (
                            <div className="flex items-center gap-2">
                              <Badge className="text-xs" style={{ background: statusStyle.bg, color: statusStyle.text, border: 'none' }}>
                                {orderStatus?.replace(/_/g, ' ') || 'Unknown'}
                              </Badge>
                              <Link to={`/app/orders/${ref.first_order.id}`}>
                                <ExternalLink className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
                              </Link>
                            </div>
                          ) : (
                            <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>No order yet</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {listTotal > 20 && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button variant="outline" size="sm" disabled={listPage === 0} onClick={() => setListPage(p => p - 1)}>
                    Previous
                  </Button>
                  <span className="text-xs self-center" style={{ color: 'var(--ink-muted)' }}>
                    Page {listPage + 1} of {Math.ceil(listTotal / 20)}
                  </span>
                  <Button variant="outline" size="sm" disabled={(listPage + 1) * 20 >= listTotal} onClick={() => setListPage(p => p + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
